import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  budgetQuery,
  resetBudget,
  saveBudget,
  type BudgetAtLimit,
  type BudgetCompanySettings,
  type BudgetPage,
} from '../../api/settingsAiAgent'
import { Spinner } from '../../ui/Spinner'
import { failed, NoteText, type Note } from './parts'

/**
 * Fila "Budgets" (Global, numai pentru un administrator de platforma - poarta
 * e pe server, fila doar nu se deseneaza altora).
 *
 * Bugetul lui Adam in bani, pe firma: suma lunii, pragul de avertizare, ce se
 * intampla la 100% (stop / numai modelul ieftin / modelul ieftin plus o suma
 * extra), alocarea implicita a unui om, furnizorii permisi si cum vede omul
 * costul. Alocarea fiecarui om sta langa accesul lui la Adam, in fila
 * "People & access" (fosta "Platform admins & Adam access"; cerinta Roxanei, 29 octombrie 2026).
 *
 * Implicitul (fara rand): buget nesetat - nimic nu se opreste -, la 100%
 * cheap_only, prag 80, toti furnizorii, USD. Alarmele intra in costul firmei,
 * dar nu se opresc.
 */

const AT_LIMIT_LABEL: Record<BudgetAtLimit, string> = {
  stop: 'Stop - Adam does not answer until next month',
  cheap_only: 'Cheapest model only',
  cheap_extra: 'Cheapest model, plus an extra amount, then stop',
}

export function BudgetsTab({ onPeople }: { onPeople?: () => void } = {}) {
  const client = useQueryClient()
  const query = useQuery(budgetQuery())
  const page = query.data ?? null
  const [companyId, setCompanyId] = useState(0)

  useEffect(() => {
    if (page && companyId === 0 && page.companies.length > 0) setCompanyId(page.companies[0].id)
  }, [page, companyId])

  if (query.isError) return <p className="text-danger">{failed(query.error)}</p>
  if (!page) return <Spinner />

  const company = page.companies.find((c) => c.id === companyId) ?? null
  const reload = () => {
    void client.invalidateQueries({ queryKey: ['settings', 'ai_agent', 'budget'] })
  }

  return (
    <div className="panel panel-default aia-budgets">
      <div className="panel-heading">
        <strong>Budgets</strong>
        <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
          what Adam may spend each month, per company and per person. A budget of 0 is not set: nothing is limited.
        </span>
      </div>
      <div className="panel-body">
        {!page.ready ? (
          <p className="text-danger" style={{ fontSize: 13 }} data-testid="budget-missing">
            {page.missing}
          </p>
        ) : null}

        <div className="form-group" style={{ maxWidth: 420 }}>
          <label htmlFor="aia-budget-company">Company</label>
          <select
            id="aia-budget-company"
            className="form-control input-sm"
            value={companyId}
            onChange={(e) => setCompanyId(Number(e.target.value))}
          >
            {page.companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.custom ? ' (set here)' : ' (default)'}
              </option>
            ))}
          </select>
        </div>

        {company ? (
          <>
            <p style={{ fontSize: 13 }} data-testid="budget-spent">
              Spent in {page.month}: <strong>${company.spent_usd.toFixed(2)}</strong>
              {company.settings.monthly_usd > 0 ? ` of $${company.settings.monthly_usd.toFixed(2)}` : ' (no budget set)'}
              {company.state !== 'ok' ? <span className={`aia-budget-state is-${company.state}`}> · {company.state}</span> : null}
            </p>
            <CompanyForm
              key={company.id + (company.custom ? ':c' : ':d')}
              page={page}
              companyId={company.id}
              custom={company.custom}
              initial={company.settings}
              onSaved={reload}
            />
            <p className="text-muted" style={{ fontSize: 12, marginTop: 16 }} data-testid="budget-people-link">
              Each person&apos;s monthly allowance is set next to their Adam access, under{' '}
              <a href="#aiaPeople" onClick={onPeople ? (e) => (e.preventDefault(), onPeople()) : undefined}>
                People &amp; access
              </a>
              . Without one, a person gets the default allowance above.
            </p>
          </>
        ) : (
          <p className="text-muted">No company.</p>
        )}
      </div>
    </div>
  )
}

function money(n: number): string {
  return Number.isFinite(n) ? String(n) : '0'
}

function CompanyForm({
  page,
  companyId,
  custom,
  initial,
  onSaved,
}: {
  page: BudgetPage
  companyId: number
  custom: boolean
  initial: BudgetCompanySettings
  onSaved(): void
}) {
  const [s, setS] = useState<BudgetCompanySettings>(initial)
  const [note, setNote] = useState<Note | null>(null)
  const disabled = !page.ready
  const all = s.allowed_labs === null

  const run = (p: Promise<{ ok: boolean; error?: string | null }>, done: string, after?: () => void) => {
    setNote({ text: '…' })
    void p
      .then((r) => {
        if (!r.ok) {
          setNote({ text: r.error ?? 'Failed', bad: true })
          return
        }
        after?.()
        setNote({ text: done })
        onSaved()
      })
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  const toggleLab = (key: string, on: boolean) => {
    const cur = s.allowed_labs ?? page.labs.map((l) => l.key)
    const next = on ? [...cur.filter((k) => k !== key), key] : cur.filter((k) => k !== key)
    setS({ ...s, allowed_labs: next })
  }

  return (
    <div style={{ fontSize: 13 }}>
      <div className="row">
        <div className="form-group col-sm-4">
          <label htmlFor="aia-budget-monthly">Monthly budget (USD, 0 = not set)</label>
          <input
            id="aia-budget-monthly"
            type="number"
            min={0}
            step="0.01"
            className="form-control input-sm"
            value={money(s.monthly_usd)}
            disabled={disabled}
            onChange={(e) => setS({ ...s, monthly_usd: Number(e.target.value) || 0 })}
          />
        </div>
        <div className="form-group col-sm-4">
          <label htmlFor="aia-budget-warn">Warn at (%)</label>
          <input
            id="aia-budget-warn"
            type="number"
            min={1}
            max={100}
            className="form-control input-sm"
            value={s.warn_pct}
            disabled={disabled}
            onChange={(e) => setS({ ...s, warn_pct: Number(e.target.value) || page.defaults.warn_pct })}
          />
        </div>
        <div className="form-group col-sm-4">
          <label htmlFor="aia-budget-userdefault">Default allowance per person (USD, 0 = unlimited)</label>
          <input
            id="aia-budget-userdefault"
            type="number"
            min={0}
            step="0.01"
            className="form-control input-sm"
            value={money(s.user_default_usd)}
            disabled={disabled}
            onChange={(e) => setS({ ...s, user_default_usd: Number(e.target.value) || 0 })}
          />
        </div>
      </div>

      <label>At 100%</label>
      {page.atLimit.map((a) => (
        <div className="radio" key={a} style={{ marginTop: 0 }}>
          <label>
            <input
              type="radio"
              name="aia-budget-at-limit"
              checked={s.at_limit === a}
              disabled={disabled}
              onChange={() => setS({ ...s, at_limit: a })}
            />{' '}
            {AT_LIMIT_LABEL[a]}
            {a !== 'stop' ? ` (${page.cheapModel})` : ''}
          </label>
        </div>
      ))}
      {s.at_limit === 'cheap_extra' ? (
        <div className="form-group" style={{ marginLeft: 20, maxWidth: 260 }}>
          <label htmlFor="aia-budget-extra">Extra amount on the cheapest model (USD)</label>
          <input
            id="aia-budget-extra"
            type="number"
            min={0}
            step="0.01"
            className="form-control input-sm"
            value={money(s.extra_usd)}
            disabled={disabled}
            onChange={(e) => setS({ ...s, extra_usd: Number(e.target.value) || 0 })}
          />
        </div>
      ) : null}

      <label style={{ marginTop: 10 }}>Allowed AI providers</label>
      <div className="checkbox" style={{ marginTop: 0 }}>
        <label>
          <input
            type="checkbox"
            checked={all}
            disabled={disabled}
            onChange={(e) => setS({ ...s, allowed_labs: e.target.checked ? null : page.labs.map((l) => l.key) })}
          />{' '}
          All providers (also the ones added later)
        </label>
      </div>
      {!all ? (
        <div style={{ marginLeft: 20 }}>
          {page.labs.map((l) => (
            <div className="checkbox" key={l.key} style={{ marginTop: 0 }}>
              <label>
                <input
                  type="checkbox"
                  aria-label={'Allow ' + l.name}
                  checked={(s.allowed_labs ?? []).includes(l.key)}
                  disabled={disabled}
                  onChange={(e) => toggleLab(l.key, e.target.checked)}
                />{' '}
                {l.name}
              </label>
            </div>
          ))}
          {(s.allowed_labs ?? []).length === 0 ? (
            <p className="text-danger" style={{ fontSize: 12 }}>
              No provider allowed: Adam will not answer anyone at this company.
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="row" style={{ marginTop: 10 }}>
        <div className="form-group col-sm-4">
          <label htmlFor="aia-budget-display">People see the cost as</label>
          <select
            id="aia-budget-display"
            className="form-control input-sm"
            value={s.display}
            disabled={disabled}
            onChange={(e) => setS({ ...s, display: e.target.value as BudgetCompanySettings['display'] })}
          >
            <option value="usd">USD</option>
            <option value="credits">Credits</option>
            <option value="hidden">Hidden</option>
          </select>
        </div>
        {s.display === 'credits' ? (
          <div className="form-group col-sm-4">
            <label htmlFor="aia-budget-credit">One credit is worth (USD)</label>
            <input
              id="aia-budget-credit"
              type="number"
              min={0.000001}
              step="0.001"
              className="form-control input-sm"
              value={money(s.credit_usd)}
              disabled={disabled}
              onChange={(e) => setS({ ...s, credit_usd: Number(e.target.value) || page.defaults.credit_usd })}
            />
          </div>
        ) : null}
      </div>
      <p className="text-muted" style={{ fontSize: 12 }}>Platform administrators always see USD.</p>

      {page.humanTiers && page.humanTiers.length > 0 ? (
        <div className="form-group" style={{ maxWidth: 420 }}>
          <label htmlFor="aia-budget-human-tier">Default tier for people</label>
          <select
            id="aia-budget-human-tier"
            className="form-control input-sm"
            value={s.human_tier ?? ''}
            disabled={disabled}
            onChange={(e) => setS({ ...s, human_tier: e.target.value })}
          >
            {page.humanTiers.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
          <p className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
            Auto starts on the cheapest allowed tier and moves up within the person&apos;s thinking depth when a tool fails
            twice, the model is unsure, the work runs long, a change on machines is next, or the person asks for a deep analysis.
            Anyone can still pick a tier by hand in Adam.
          </p>
        </div>
      ) : null}

      <div style={{ marginTop: 8 }}>
        <button type="button" className="btn btn-primary btn-sm" disabled={disabled} onClick={() => run(saveBudget(companyId, s), 'Saved')}>
          Save
        </button>{' '}
        {custom ? (
          <button
            type="button"
            className="btn btn-default btn-sm"
            disabled={disabled}
            onClick={() => run(resetBudget(companyId), 'Back to the default', () => setS(page.defaults))}
          >
            Reset to default
          </button>
        ) : (
          <span className="text-muted">This company runs on the default.</span>
        )}{' '}
        <NoteText note={note} />
      </div>
    </div>
  )
}
